// eBay connection + listing/offer/promoted defaults page.
//
// This page is the destination of the OAuth callback (server-side
// `/api/ebay/auth/callback` → 302 `/connect-ebay?ebay=connected`).
// It was extracted out of ProfilePage (iter_45) so the profile screen stays
// focused on user-level preferences (brand rules, price targets, sign out)
// while platform-level settings live here.

import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, Check, CheckCircle, Storefront, Warning, XCircle,
} from "@phosphor-icons/react";
import { ebayAPI, prefsAPI } from "@/lib/api";
import BottomNav from "@/components/app/BottomNav";
import { SectionHeader, PolicySelect, CampaignManager } from "@/pages/ProfilePage";
import { useConfirm } from "@/components/app/ConfirmDialog";

export default function ConnectEbayPage() {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [searchParams, setSearchParams] = useSearchParams();
  const callbackStatus = searchParams.get("ebay"); // "connected" | "error" | "declined"
  const fromOnboarding = searchParams.get("onboarding") === "1";

  const [prefs, setPrefs] = useState(null);
  const [ebayStatus, setEbayStatus] = useState(null);
  const [ebayPolicies, setEbayPolicies] = useState(null);
  const [loading, setLoading] = useState(true);
  const [banner, setBanner] = useState(null); // {kind, text}

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [p, e] = await Promise.all([
        prefsAPI.get(),
        ebayAPI.authStatus().catch(() => ({ data: { connected: false } })),
      ]);
      setPrefs(p.data);
      setEbayStatus(e.data);
      if (e.data?.connected) {
        ebayAPI.policies().then(({ data }) => setEbayPolicies(data)).catch(() => {});
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Translate the OAuth callback querystring into an in-page banner, then
  // clean the URL so a refresh doesn't re-show the toast.
  useEffect(() => {
    if (!callbackStatus) return;
    if (callbackStatus === "connected") {
      setBanner({
        kind: "success",
        text: fromOnboarding
          ? "Connected! Now set your listing defaults below — these auto-fill every new listing."
          : "eBay reconnected successfully.",
      });
    } else if (callbackStatus === "declined") {
      setBanner({ kind: "warn", text: "You cancelled the eBay sign-in. You can try again any time." });
    } else if (callbackStatus === "error") {
      const reason = searchParams.get("reason") || "unknown";
      setBanner({ kind: "error", text: `eBay sign-in failed (${reason}). Try again or contact support.` });
    }
    // Clean params, keep onboarding flag if relevant (no — it's done with)
    const fresh = new URLSearchParams(searchParams);
    fresh.delete("ebay");
    fresh.delete("reason");
    fresh.delete("onboarding");
    setSearchParams(fresh, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const savePrefs = async (patch) => {
    try {
      const { data } = await prefsAPI.update(patch);
      setPrefs(data);
    } catch {}
  };

  const handleConnect = async () => {
    try {
      const { data } = await ebayAPI.connect();
      if (data?.auth_url) window.location.href = data.auth_url;
    } catch {
      setBanner({ kind: "error", text: "Could not start eBay sign-in. Please try again." });
    }
  };

  const handleDisconnect = async () => {
    const ok = await confirm({
      title: "Disconnect eBay?",
      message: "You'll need to sign in to eBay again before you can list. Your account stays — just the connection is severed.",
      confirmLabel: "Disconnect",
      destructive: true,
    });
    if (!ok) return;
    try {
      await ebayAPI.disconnect();
      setEbayStatus({ connected: false });
      setEbayPolicies(null);
      setBanner({ kind: "success", text: "eBay disconnected." });
    } catch {
      setBanner({ kind: "error", text: "Could not disconnect. Try again." });
    }
  };

  if (loading || !prefs) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <div className="font-heading text-sm font-bold uppercase tracking-wider text-neutral-500">Loading…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 pb-24" data-testid="connect-ebay-page">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-white border-b-4 border-black py-4 px-5 flex items-center gap-3">
        <button
          onClick={() => navigate("/profile")}
          className="p-1.5 border-2 border-black hover:bg-neutral-100"
          data-testid="connect-ebay-back"
        >
          <ArrowLeft size={18} weight="bold" />
        </button>
        <h1 className="font-heading text-xl font-black uppercase tracking-tight flex-1">
          Connect eBay
        </h1>
      </div>

      <div className="p-6 space-y-7 max-w-2xl mx-auto">
        {banner && (
          <div
            className={`border-2 p-3 flex items-start gap-2 ${
              banner.kind === "success" ? "border-[#00E676] bg-green-50" :
              banner.kind === "warn"    ? "border-amber-400 bg-amber-50" :
                                          "border-red-500 bg-red-50"
            }`}
            data-testid={`connect-ebay-banner-${banner.kind}`}
          >
            {banner.kind === "success" ? (
              <CheckCircle size={18} weight="bold" className="text-green-700 shrink-0" />
            ) : banner.kind === "warn" ? (
              <Warning size={18} weight="bold" className="text-amber-700 shrink-0" />
            ) : (
              <XCircle size={18} weight="bold" className="text-red-700 shrink-0" />
            )}
            <div className="font-body text-sm flex-1">{banner.text}</div>
            <button onClick={() => setBanner(null)} className="font-bold">×</button>
          </div>
        )}

        {/* Connect / Disconnect */}
        <div>
          <SectionHeader title="eBay Account" />
          <div className="border-2 border-black p-4">
            <div className="flex items-center gap-2 mb-3">
              <Storefront size={18} weight="bold" />
              <span className="font-body text-sm font-semibold">
                {ebayStatus?.connected ? "Connected" : "Not connected"}
              </span>
              {ebayStatus?.connected && (
                <Check size={16} weight="bold" className="text-[#00E676]" />
              )}
            </div>
            {ebayStatus?.connected ? (
              <button
                onClick={handleDisconnect}
                className="w-full p-3 bg-white text-red-600 font-heading font-bold uppercase text-sm border-2 border-red-300 hover:bg-red-50"
                data-testid="connect-ebay-disconnect"
              >
                Disconnect eBay
              </button>
            ) : (
              <button
                onClick={handleConnect}
                className="w-full p-3 bg-[#002FA7] text-white font-heading font-bold uppercase text-sm border-2 border-black shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] active:translate-y-[1px] active:shadow-none transition-all"
                data-testid="connect-ebay-connect"
              >
                Connect eBay
              </button>
            )}
          </div>
        </div>

        {/* Listing Defaults */}
        {ebayStatus?.connected && ebayPolicies && (
          <div>
            <SectionHeader title="Listing Defaults" />
            <p className="font-body text-xs text-neutral-500 mb-3">
              Set default policies for new eBay listings. You can override these per listing.
            </p>
            <div className="space-y-3">
              <PolicySelect
                label="Shipping Policy"
                options={ebayPolicies.fulfillment || []}
                value={prefs?.ebay_default_fulfillment || ""}
                onChange={(val) => savePrefs({ ebay_default_fulfillment: val })}
                testid="connect-ebay-default-fulfillment"
              />
              <PolicySelect
                label="Payment Policy"
                options={ebayPolicies.payment || []}
                value={prefs?.ebay_default_payment || ""}
                onChange={(val) => savePrefs({ ebay_default_payment: val })}
                testid="connect-ebay-default-payment"
              />
              <PolicySelect
                label="Return Policy"
                options={ebayPolicies.return || []}
                value={prefs?.ebay_default_return || ""}
                onChange={(val) => savePrefs({ ebay_default_return: val })}
                testid="connect-ebay-default-return"
              />
            </div>
          </div>
        )}

        {/* Offer Defaults */}
        {ebayStatus?.connected && (
          <div>
            <SectionHeader title="Offer Defaults" />
            <div className="border-2 border-neutral-200 p-3 space-y-3">
              <label className="flex items-center justify-between cursor-pointer" data-testid="connect-ebay-allow-offers">
                <div>
                  <span className="font-heading text-xs font-bold uppercase tracking-wide">Allow Offers</span>
                  <p className="font-body text-[10px] text-neutral-400 mt-0.5">Buyers can send offers. You can accept, counter, or decline.</p>
                </div>
                <input
                  type="checkbox"
                  checked={prefs?.allow_offers_default ?? true}
                  onChange={(e) => savePrefs({ allow_offers_default: e.target.checked })}
                  className="w-5 h-5 border-2 border-black accent-black shrink-0"
                />
              </label>
              {(prefs?.allow_offers_default ?? true) && (
                <div className="grid grid-cols-2 gap-4 pt-2 border-t border-neutral-200">
                  <NumberField
                    label="Auto Accept (% off)"
                    help="Auto-accept offers within this % of price. Leave blank to review all."
                    value={prefs?.offer_auto_accept_pct ?? ""}
                    onChange={(v) => setPrefs({ ...prefs, offer_auto_accept_pct: v })}
                    onBlur={() => savePrefs({ offer_auto_accept_pct: prefs?.offer_auto_accept_pct ?? null })}
                    testid="connect-ebay-offer-auto-accept"
                  />
                  <NumberField
                    label="Min Offer (% off)"
                    help="Auto-decline offers below this %. Leave blank to receive all."
                    value={prefs?.offer_min_pct ?? ""}
                    onChange={(v) => setPrefs({ ...prefs, offer_min_pct: v })}
                    onBlur={() => savePrefs({ offer_min_pct: prefs?.offer_min_pct ?? null })}
                    testid="connect-ebay-offer-min-pct"
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Promoted Listings */}
        {ebayStatus?.connected && (
          <div>
            <SectionHeader title="Promoted Listings" />
            <p className="font-body text-xs text-neutral-500 mb-3">
              Manage your eBay ad campaigns. Items added get boosted placement — you only pay when they sell.
            </p>
            <CampaignManager />
          </div>
        )}
      </div>

      <BottomNav />
    </div>
  );
}

function NumberField({ label, help, value, onChange, onBlur, testid }) {
  return (
    <div>
      <label className="font-body text-xs font-semibold tracking-[0.1em] uppercase text-neutral-700">
        {label}
      </label>
      <div className="flex items-center gap-2 mt-1">
        <input
          type="number"
          value={value}
          onChange={(e) => {
            const raw = e.target.value;
            onChange(raw === "" ? null : (parseInt(raw, 10) || 0));
          }}
          onBlur={onBlur}
          min="1"
          max="99"
          className="w-20 border-2 border-black bg-white px-3 py-2 font-heading text-lg focus:outline-none focus:ring-4 focus:ring-black/20"
          data-testid={testid}
        />
        <span className="font-heading text-lg font-bold">%</span>
      </div>
      <p className="font-body text-[10px] text-neutral-400 mt-1">{help}</p>
    </div>
  );
}
